/**
 * Tour steps config — driver.js DriveStep[]
 *
 * Emotional design: mỗi step nói từ góc nhìn Lan (office worker FDI/Legal),
 * pain cụ thể → solution cụ thể với số đo thật. Không feature bullet.
 *
 * Industry: template variable, fill khi user pick ở welcome modal.
 */

import type { DriveStep } from 'driver.js';

export function buildTourSteps(industry: string): DriveStep[] {
  return [
    // ── Group 1: Data handling (lưu trữ → privacy) ──
    {
      element: '[data-tour="workspace"]',
      popover: {
        title: 'Thư mục của bạn — két sắt riêng',
        description: `
          Hợp đồng nhạy cảm, báo cáo nội bộ? Yên tâm.<br><br>
          File lưu <b>trong folder bạn vừa chọn</b>, không upload lên server.
          Đổi máy: copy folder qua là tiếp tục làm — không mất gì.
        `,
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="privacy-mode"]',
      popover: {
        title: 'Dịch Ngầm vs Dịch Ẩn Danh — bạn chọn',
        description: `
          <b>Dịch Ngầm</b> (mặc định): file lưu máy bạn + backup tạm lên server mã hoá
          để nếu mất điện giữa chừng, mở lại vẫn tiếp tục được.<br><br>
          <b>Dịch Ẩn Danh</b>: <em>zero server touch</em> — không backup, không log nội dung.
          Hợp đồng tối mật? Chọn chế độ này. Đánh đổi: mất điện giữa chừng = dịch lại từ đầu.
        `,
        side: 'right',
        align: 'start',
      },
    },

    // ── Group 2: Features (Dịch tài liệu CHÍNH → Huấn luyện → Thuật ngữ) ──
    {
      element: '[data-tour="nav-translating"]',
      popover: {
        title: 'Dịch tài liệu — tính năng chính bạn dùng mỗi ngày',
        description: `
          Đây là <b>tab cốt lõi của app</b>. Bạn vào đây để:<br><br>
          1. Tạo dự án theo khách / loại doc<br>
          2. Kéo thả file DOCX/PPTX/TXT<br>
          3. Bấm "Dịch" → AI xử lý 10 trang/phút, giữ format<br>
          4. Tải bản dịch về — copy thẳng vào email sếp.<br><br>
          Hợp đồng 50 trang xong trong 5 phút.
        `,
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="nav-training"]',
      popover: {
        title: 'Huấn luyện — AI học phong cách công ty bạn',
        description: `
          Công ty bạn có 5-10 báo cáo cũ đã dịch chuẩn rồi? Upload cặp bản gốc VI + bản dịch EN vào đây.<br><br>
          AI tự so sánh, học phong cách: câu ngắn hay dài, formal hay casual, cách xưng hô. Lần sau dịch
          file mới — output <b>giống tay sếp bạn duyệt hàng năm</b>, không phải bản Google Translate chung chung.
        `,
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="nav-terminology"]',
      popover: {
        title: 'Từ điển thuật ngữ — để "bên bán" luôn là "Seller"',
        description: `
          Đây là <b>từ điển dùng chung</b> cho mọi dự án. Tự thêm từng thuật ngữ
          hoặc import Excel/CSV nếu công ty bạn đã có sẵn bảng.<br><br>
          Khỏi lo AI dịch "đối tác chiến lược" lúc thì "strategic partner", lúc "alliance partner".
          Đã có trong từ điển → dịch nhất quán toàn bộ file.
        `,
        side: 'right',
        align: 'start',
      },
    },

    // ── Group 3: Credits (xăng + nhận quà) ──
    {
      element: '[data-tour="credits"]',
      popover: {
        title: '200K credits tặng bạn thử — đủ 1 báo cáo dày',
        description: `
          Credits là "xăng" để dịch. Ở góc này bạn thấy số credits còn lại.<br><br>
          Dịch 1 báo cáo dày 25 trang ≈ <b>74K credits</b> — sub-cà phê.
          200K credits welcome đủ trải nghiệm 1 báo cáo complete (dùng trong 30 ngày).
        `,
        side: 'right',
        align: 'end',
      },
    },
    {
      element: '[data-tour="gift"]',
      popover: {
        title: 'Muốn thêm credits? Nhấn "Nhận quà tặng"',
        description: `
          Banner tím <b>"NHẬN QUÀ TẶNG"</b> phía trên — bấm vào để nhận thêm token free.<br><br>
          Không phí, không ràng buộc. Bonus để bạn dịch thêm vài file nữa trải nghiệm đủ.
        `,
        side: 'right',
        align: 'start',
      },
    },

    // ── Group 4: CTA cuối — tạo project ──
    {
      element: '[data-tour="create-project"]',
      popover: {
        title: 'Sẵn sàng rồi — tạo dự án đầu tiên',
        description: `
          Bấm đây tạo dự án. VD: <b>"Hợp đồng FDI Q2"</b> cho khách A, <b>"Báo cáo tuần ABC"</b> cho khách B.<br><br>
          AI dịch sẽ tune theo ngành <b>${industry}</b> bạn vừa chọn — hiểu ngữ cảnh
          hợp đồng / báo cáo / báo cáo chuyên môn, giữ phong cách phù hợp.<br><br>
          <em style="color:#64748b;font-size:12px;">Tạo dự án xong, kéo thả file vào là AI dịch. 10 trang/phút, giữ nguyên chart/bảng/layout.</em>
        `,
        side: 'bottom',
        align: 'end',
      },
    },
  ];
}

// Steps 5-7 (upload, glossary, translate button) defer v2 — cần logic multi-page
// vì element nằm trong ProjectDetail view, tour phải pause khi user navigate.
// Pattern: chunk tour thành "sidebar tour" (current) + "project tour" (triggered
// khi user mở project đầu tiên, check localStorage.tour_project_done).

/**
 * Industries — emotional framing (không "generic enterprise" BS).
 * Mỗi industry có 1 benefit cụ thể Lan nghe sẽ cảm.
 */
export const INDUSTRIES = [
  {
    id: 'Legal',
    name: 'Pháp lý / Hợp đồng',
    hint: 'Hợp đồng thương mại, điều khoản',
    icon: 'scale',
  },
  {
    id: 'Finance',
    name: 'Tài chính / Kế toán',
    hint: 'Báo cáo, BCTC, kế toán',
    icon: 'briefcase',
  },
  {
    id: 'Audit',
    name: 'Kiểm toán',
    hint: 'Báo cáo FDI, audit report',
    icon: 'chart',
  },
  {
    id: 'Export',
    name: 'Xuất nhập khẩu',
    hint: 'Invoice, PO, hợp đồng giao hàng',
    icon: 'package',
  },
  {
    id: 'Real Estate',
    name: 'Bất động sản',
    hint: 'Listing, hợp đồng mua bán',
    icon: 'building',
  },
  {
    id: 'Other',
    name: 'Khác',
    hint: 'HR, marketing, SOP nội bộ',
    icon: 'edit',
  },
] as const;

export type IndustryId = typeof INDUSTRIES[number]['id'];
